/**
 * StatsScreen — Phase 2 patch 4: Weekly stats overview.
 *
 * Biểu đồ bar 7 ngày bằng SVG tự build (không cần recharts).
 * Backend: GET /v1/stats/week.
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { useTelegramBackButton } from "../hooks/useTelegramBackButton";
import { api, extractErrorMessage } from "../lib/api";
import { BottomNav } from "../scenes/MainCafe/components/BottomNav";

interface DayStat {
  date: string; // YYYY-MM-DD
  gross_revenue: number;
  orders_served: number;
  orders_correct: number;
}

interface WeekStats {
  days: DayStat[];
  total_revenue: number;
  total_orders: number;
  current_streak: number;
}

const DAY_LABEL_VI = ["CN", "T2", "T3", "T4", "T5", "T6", "T7"];

function dayShortLabel(dateStr: string): string {
  // dayOfWeek 0 = Sunday
  const d = new Date(dateStr);
  return DAY_LABEL_VI[d.getDay()];
}

function dayShortDate(dateStr: string): string {
  const d = new Date(dateStr);
  return `${d.getDate()}/${d.getMonth() + 1}`;
}

export function StatsScreen() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  useTelegramBackButton("/");

  const [stats, setStats] = useState<WeekStats | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const res = await api.get<{ data: WeekStats }>("/stats/week");
        if (!cancelled) setStats(res.data.data);
      } catch (err) {
        if (!cancelled) setError(extractErrorMessage(err));
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  // Chart dimensions
  const CHART_W = 320;
  const CHART_H = 160;
  const BAR_GAP = 8;
  const PADDING_LEFT = 28;
  const PADDING_BOTTOM = 24;

  const maxRevenue = stats
    ? Math.max(...stats.days.map((d) => d.gross_revenue), 1)
    : 1;
  const barCount = 7;
  const barWidth =
    (CHART_W - PADDING_LEFT - BAR_GAP * (barCount - 1)) / barCount;

  return (
    <div className="w-full h-full max-w-[480px] mx-auto flex flex-col bg-cream-50 relative overflow-hidden safe-area-top">
      {/* Header */}
      <div className="px-4 py-3 bg-coffee-900 text-cream-50 flex items-center justify-between">
        <button
          type="button"
          onClick={() => navigate("/")}
          className="text-sm opacity-80 hover:opacity-100"
        >
          {t("stats.back")}
        </button>
        <h1 className="text-base font-bold font-baloo">
          {t("stats.screen_title")}
        </h1>
        <div className="w-10" />
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
        {error && (
          <div className="bg-red-stool/10 border border-red-stool/30 text-red-stool rounded-lg px-3 py-2 text-sm">
            {error}
          </div>
        )}

        {stats === null ? (
          <div className="text-center text-coffee-700 py-8">
            {t("stats.loading")}
          </div>
        ) : (
          <>
            {/* Summary cards */}
            <div className="grid grid-cols-3 gap-2">
              <SummaryCard
                icon="🔥"
                label={t("stats.streak")}
                value={t("stats.streak_days", { count: stats.current_streak })}
              />
              <SummaryCard
                icon="💰"
                label={t("stats.revenue") + " (" + t("stats.week") + ")"}
                value={stats.total_revenue.toLocaleString()}
              />
              <SummaryCard
                icon="📋"
                label={t("stats.orders") + " (" + t("stats.week") + ")"}
                value={stats.total_orders.toLocaleString()}
              />
            </div>

            {/* Weekly chart */}
            <div className="bg-white rounded-2xl p-4 shadow-soft-amber border border-coffee-700/10">
              <div className="text-sm font-semibold text-coffee-900 mb-2">
                {t("stats.revenue")} — {t("stats.week")}
              </div>
              {stats.total_revenue === 0 ? (
                <div className="text-center text-coffee-700/60 text-sm py-6">
                  {t("stats.empty_week")}
                </div>
              ) : (
                <svg
                  viewBox={`0 0 ${CHART_W} ${CHART_H}`}
                  className="w-full h-auto"
                  preserveAspectRatio="xMidYMid meet"
                >
                  {/* Y-axis label */}
                  <text
                    x={4}
                    y={14}
                    fontSize="9"
                    fill="rgba(45,32,21,0.6)"
                  >
                    {maxRevenue.toLocaleString()}
                  </text>
                  <text
                    x={4}
                    y={CHART_H - PADDING_BOTTOM - 2}
                    fontSize="9"
                    fill="rgba(45,32,21,0.6)"
                  >
                    0
                  </text>
                  {/* Baseline */}
                  <line
                    x1={PADDING_LEFT}
                    y1={CHART_H - PADDING_BOTTOM}
                    x2={CHART_W}
                    y2={CHART_H - PADDING_BOTTOM}
                    stroke="rgba(45,32,21,0.15)"
                    strokeWidth="1"
                  />
                  {/* Bars */}
                  {stats.days.map((day, i) => {
                    const h =
                      (day.gross_revenue / maxRevenue) *
                      (CHART_H - PADDING_BOTTOM - 8);
                    const x = PADDING_LEFT + i * (barWidth + BAR_GAP);
                    const y = CHART_H - PADDING_BOTTOM - h;
                    const isToday = i === stats.days.length - 1;
                    return (
                      <g key={day.date}>
                        <rect
                          x={x}
                          y={y}
                          width={barWidth}
                          height={h}
                          rx="3"
                          fill={isToday ? "#E8B547" : "#A0784D"}
                        />
                        {/* Value label trên bar nếu đủ chỗ */}
                        {day.gross_revenue > 0 && h > 12 && (
                          <text
                            x={x + barWidth / 2}
                            y={y - 2}
                            fontSize="8"
                            textAnchor="middle"
                            fill="rgba(45,32,21,0.8)"
                          >
                            {day.gross_revenue}
                          </text>
                        )}
                        {/* Day label */}
                        <text
                          x={x + barWidth / 2}
                          y={CHART_H - PADDING_BOTTOM + 12}
                          fontSize="9"
                          textAnchor="middle"
                          fill="rgba(45,32,21,0.7)"
                          fontWeight={isToday ? 700 : 400}
                        >
                          {dayShortLabel(day.date)}
                        </text>
                        <text
                          x={x + barWidth / 2}
                          y={CHART_H - PADDING_BOTTOM + 21}
                          fontSize="7"
                          textAnchor="middle"
                          fill="rgba(45,32,21,0.4)"
                        >
                          {dayShortDate(day.date)}
                        </text>
                      </g>
                    );
                  })}
                </svg>
              )}
            </div>

            {/* Daily detail list */}
            <div className="bg-white rounded-2xl p-3 shadow-soft-amber border border-coffee-700/10">
              <div className="text-sm font-semibold text-coffee-900 mb-2 px-1">
                {t("stats.orders")} — {t("stats.week")}
              </div>
              <div className="space-y-1">
                {stats.days.map((day, i) => {
                  const isToday = i === stats.days.length - 1;
                  return (
                    <div
                      key={day.date}
                      className={`flex items-center justify-between text-xs px-2 py-1.5 rounded ${
                        isToday ? "bg-yellow-light/20 font-bold" : ""
                      }`}
                    >
                      <span className="text-coffee-700">
                        {dayShortLabel(day.date)} {dayShortDate(day.date)}
                      </span>
                      <div className="flex gap-3 text-coffee-900">
                        <span>☕ {day.gross_revenue}</span>
                        <span>📋 {day.orders_served}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </>
        )}
      </div>

      <BottomNav />
    </div>
  );
}

function SummaryCard({
  icon,
  label,
  value,
}: {
  icon: string;
  label: string;
  value: string;
}) {
  return (
    <div className="bg-white rounded-xl p-2 shadow-soft-amber border border-coffee-700/10 text-center">
      <div className="text-lg">{icon}</div>
      <div className="text-[9px] text-coffee-700/60 uppercase tracking-wide mt-0.5 leading-tight">
        {label}
      </div>
      <div className="text-sm font-baloo font-bold text-coffee-900 mt-1">
        {value}
      </div>
    </div>
  );
}
